import React, { useId, useState } from "react";
import { cn } from "../../../common/u1/cn";
import { CompanyMark } from "../../../common/u1/CompanyMark";
import { ChevronDown } from "../../../../assets/ui/icons";
import { TrackedStatusBadge } from "./TrackedStatusBadge";
import { ProfileCompletionBar } from "./ProfileCompletionBar";
import { TrackedUpdatesPanel } from "./TrackedUpdatesPanel";
import {
  trackedAddress,
  trackedChanges,
  trackedProfileId,
  trackedStatus,
} from "./enterpriseRows";

/* The buyers an enterprise tracks. */
const CELL = "px-4 py-3.5 align-middle text-[13.5px] font-sans sm:px-5";

const HEAD = cn(
  "px-4 py-2.5 text-left text-[10.5px] font-bold uppercase tracking-[.07em]",
  "text-[var(--muted)] font-sans sm:px-5"
);

export const TrackedBusinessRow = ({ row, onOpenProfile, onOpenChanges, onCloseChanges }) => {
  const profileId = trackedProfileId(row);
  const changes = trackedChanges(row);
  const address = trackedAddress(row);
  const [open, setOpen] = useState(false);
  const panelId = `${useId()}-updates`;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    /* Transcribed from the activity log's mount effect: looking at the changes
       is what marks them seen. Fired only on open. */
    if (next) onOpenChanges(row);
    /* The list is refreshed on the way out rather than on the way in — a
       refetch while the panel is open would empty it under the reader. */
    else onCloseChanges(row);
  };

  return (
    <>
    {/* The business row stays white when open; the ground behind the panel is
        what changes, so the card reads as sitting under the row. */}
    <tr className="border-t border-[var(--line)]">
      <td className={cn(CELL, "min-w-[240px]")}>
        <span className="flex items-center gap-3">
          <span className="shrink-0">
            {/* One navy for every mark, as the design draws them and as the account menu already does: a tracked list reads as one set, where five hashed palette colours... */}
            <CompanyMark
              company={{
                name: row?.company_name,
                logo: row?.logo,
                companyID: trackedProfileId(row),
              }}
              member={Boolean(row?.verified)}
              size="xs"
              tone="brand"
            />
          </span>

          {/* Transcribed: only a business with an id behind it can be opened,
              and the live screen makes the unopenable ones plainly inert. */}
          {profileId ? (
            <button
              type="button"
              onClick={() => onOpenProfile(profileId)}
              className={cn(
                "text-left text-[14px] font-bold text-[var(--ink)] font-sans",
                "transition-colors hover:text-[var(--coral)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]"
              )}
            >
              {row?.company_name}
            </button>
          ) : (
            <span
              className="text-[14px] font-bold text-[var(--ink)] font-sans"
              title="This business has no profile to open yet"
            >
              {row?.company_name}
            </span>
          )}
        </span>
      </td>

      <td className={cn(CELL, "text-[var(--ink-2)]")}>{address}</td>

      <td className={CELL}>
        <TrackedStatusBadge status={trackedStatus(row)} />
      </td>

      <td className={CELL}>
        {changes.actionable ? (
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            aria-controls={panelId}
            className={cn(
              "inline-flex items-center gap-1.5 text-[13.5px] font-semibold",
              "text-[var(--ink)] font-sans transition-colors hover:text-[var(--coral)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]"
            )}
          >
            <span
              aria-hidden="true"
              className="h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--warning)]"
            />
            {changes.label}
            <span className="shrink-0 text-[var(--muted)]">
              <ChevronDown open={open} />
            </span>
          </button>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[13.5px] text-[var(--muted)] font-sans">
            <span
              aria-hidden="true"
              className="h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--muted-2)]"
            />
            {changes.label}
          </span>
        )}
      </td>

      <td className={cn(CELL, "min-w-[190px]")}>
        <ProfileCompletionBar percent={row?.percent} />
      </td>
    </tr>

    {/* The panel spans the table rather than sitting inside one cell — a column-width list of changes would be unreadable — but it is inset rather than full-bleed:... */}
    {open && (
      <tr className="bg-[var(--bg)]">
        <td colSpan={5} className="px-4 pb-4 pt-0 sm:px-5">
          <div className="overflow-hidden rounded-xl border border-[var(--line)] bg-white">
            <TrackedUpdatesPanel id={panelId} updates={row?.updates} />
          </div>
        </td>
      </tr>
    )}
    </>
  );
};

export const TrackedBusinessesTable = ({
  rows = [],
  onOpenProfile = () => {},
  onOpenChanges = () => {},
  onCloseChanges = () => {},
  emptyMessage = "No businesses to show.",
}) => (
  /* Five columns of names, addresses and meters will not fit a phone, so the
     table scrolls inside its own box rather than widening the page. */
  <div className="overflow-x-auto">
    <table className="w-full min-w-[820px] border-collapse">
      <thead className="bg-[var(--bg)]">
        <tr>
          <th scope="col" className={HEAD}>Business name</th>
          <th scope="col" className={HEAD}>Address</th>
          <th scope="col" className={HEAD}>Status</th>
          <th scope="col" className={HEAD}>Recent changes</th>
          <th scope="col" className={HEAD}>Profile completion</th>
        </tr>
      </thead>

      <tbody>
        {rows.length === 0 ? (
          <tr className="border-t border-[var(--line)]">
            <td
              colSpan={5}
              className="px-5 py-10 text-center text-[13.5px] text-[var(--muted)] font-sans"
            >
              {emptyMessage}
            </td>
          </tr>
        ) : (
          rows.map((row, i) => (
            <TrackedBusinessRow
              /* Tracked rows carry no stable id of their own in every shape,
                 so the company id leads and the index is the fallback. */
              key={trackedProfileId(row) || `tracked-${i}`}
              row={row}
              onOpenProfile={onOpenProfile}
              onOpenChanges={onOpenChanges}
              onCloseChanges={onCloseChanges}
            />
          ))
        )}
      </tbody>
    </table>
  </div>
);

export default TrackedBusinessesTable;
